import React, { createContext, useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import authService from '../services/authService';

// Create Authentication Context
export const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  // Check if token exists and validate on app load
  useEffect(() => {
    const initAuth = async () => {
      try {
        // Check if user is authenticated (has valid token)
        if (authService.isAuthenticated()) {
          // Get user info from token
          const user = authService.getCurrentUser();
          setCurrentUser(user);
          
          // Verify token with server (optional extra validation)
          const isValid = await authService.verifyToken();
          if (!isValid) {
            // Try to refresh the token
            try {
              const refreshedUser = await authService.refreshToken();
              setCurrentUser(refreshedUser);
            } catch (refreshError) {
              // Refresh failed, logout
              console.error('Token refresh failed:', refreshError);
              authService.logout();
              setCurrentUser(null);
            }
          }
        }
      } catch (err) {
        console.error('Auth initialization error:', err);
        authService.logout();
        setCurrentUser(null);
      } finally {
        setLoading(false);
      }
    };

    initAuth();
  }, []);

  // Login function
  const login = async (username, password) => {
    setError(null);
    try {
      const user = await authService.login(username, password);
      setCurrentUser(user);
      return user;
    } catch (err) {
      setError(err.message || 'Failed to login');
      throw err;
    }
  };

  // Logout function
  const logout = () => {
    authService.logout();
    setCurrentUser(null);
    navigate('/login');
  };

  // Refresh token function
  const refreshToken = async () => {
    try {
      const refreshedUser = await authService.refreshToken();
      setCurrentUser(refreshedUser);
      return true;
    } catch (err) {
      console.error('Token refresh failed:', err);
      setError('Session expired. Please login again.');
      authService.logout();
      setCurrentUser(null);
      return false;
    }
  };
  
  // Authentication context value
  const value = {
    currentUser,
    loading,
    error,
    login,
    logout,
    refreshToken,
    isAuthenticated: !!currentUser
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

// Custom hook to use auth context
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};